//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { useGetIsBulkSelected } from '@hooks/useGetIsBulkSelected';
import { IDirections } from '@models/IDirections';
import ItemId from '@models/ItemId';
import { UniversalItemType } from '@models/UniversalItemType';
import { Mixins } from '@theme/mixins';

interface Props
  extends React.HTMLAttributes<HTMLDivElement>,
    IDirections<React.ReactElement> {
  image: React.ReactElement;
  itemType?: UniversalItemType;
  id?: ItemId;
}

/**
 *
 * The MediaCard molecule is an image on a sheet with optional
 * metadata positioned in any of the four supported directions
 * relative to that image. The molecule itself uses the provided
 * component's dimensions as a basis for its size, but will also
 * shrink if used in a parent with a specified width or height.
 */
export const MediaCard = (props: Props) => {
  const isItemSelected = useGetIsBulkSelected(props.itemType, props.id);
  const isProduct = props.itemType === UniversalItemType.Product;
  return (
    <RawMediaCard
      {...props}
      isItemSelected={isItemSelected}
      isProduct={isProduct}
    />
  );
};

export const RawMediaCard = ({
  bottom,
  className,
  image,
  isItemSelected,
  isProduct,
  left,
  right,
  top,
}: Props & {
  isItemSelected: boolean;
  isProduct: boolean;
}) => {
  return (
    <Sheet
      className={className}
      isItemSelected={isItemSelected}
      isProduct={isProduct}
    >
      {top && <MetadataRow>{top}</MetadataRow>}
      <MediaRow>
        {left && <MetadataColumn>{left}</MetadataColumn>}
        <MediaColumn>{image}</MediaColumn>
        {right && <MetadataColumn>{right}</MetadataColumn>}
      </MediaRow>
      {bottom && <MetadataRow>{bottom}</MetadataRow>}
    </Sheet>
  );
};

const Sheet = styled.div<{ isItemSelected: boolean; isProduct: boolean }>(
  ({ isItemSelected, isProduct, theme }) => `
  ${Mixins.Sheet.Low}
  display: flex;
  flex-direction: column;
  ${
    !isProduct &&
    `
    transition: box-shadow 0.2s, transform 0.2s;
    :hover {
      box-shadow: ${theme.boxShadows[2]};
    }
  `
  };
  ${
    isItemSelected &&
    `
    transform: scale(1.02);
    box-shadow: ${theme.boxShadows[3]} !important;
    z-index: 1;
  `
  };
`,
);

const MetadataRow = styled.div`
  min-height: 0;
`;

const MetadataColumn = styled.div`
  min-width: 0;
  flex: 1;
`;

const MediaRow = styled.div`
  flex: 0 0 auto;
  min-height: 0;
  display: flex;
`;

const MediaColumn = styled.div`
  flex: 0 0 auto;
`;

export default MediaCard;
